import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { login as apiLogin, logout as apiLogout, apiGet, loadStoredRefreshToken, setTokens } from '../services/api';

interface User {
  id: string;
  username: string;
  email: string;
  full_name: string | null;
  is_superuser: boolean;
  role: 'admin' | 'user';
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Try to restore session from stored refresh token
    const initAuth = async () => {
      const refreshToken = loadStoredRefreshToken();
      if (refreshToken) {
        try {
          // Try to get current user (will trigger token refresh if needed)
          const userData = await apiGet<User>('/api/auth/me');
          setUser(userData);
        } catch {
          // Token invalid or expired
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (username: string, password: string) => {
    const data = await apiLogin(username, password);
    setTokens(data.access_token, data.refresh_token);
    const userData = await apiGet<User>('/api/auth/me');
    setUser(userData);
  };

  const logout = async () => {
    await apiLogout();
    setUser(null);
  };

  const isAdmin = user?.role === 'admin' || user?.is_superuser || false;

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        isAdmin,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
